import * as Immutable from 'immutable'
import * as React from 'react'
import { IntlProvider } from 'react-intl'
import {
  Action,
  any,
  async,
  AsyncState,
  fromJSX,
  loadingAsyncState,
  none,
  nothing,
  Option,
  some,
  stateful,
  Widget
} from 'widgets-for-react'
import { LoaderCircle } from '../../accommodations/models/loading'
import { getCompliance } from '../../api/online_payment_platform'
import { getIdealIssuers, IdealIssuerResponse } from '../../api/payments'
import { ComplianceResponse } from '../../types/online_payment_platform'
import { IdealIssuer } from '../../types/types'
import * as DL from '../../utils/datalayer'
import {
  ChooseOptionComponent,
  ChooseOptionProps
} from '../shared/choose_option'
import { contactRequirement } from './onboarding'

type Props = {
  id: number
  idinImage: string
}

type MethodKey = 'idin' | 'document'
type Method = {
  id: number
  name: MethodKey
  label: string
  extraSelected: string
}

const methods = (issuerSelected: string): Method[] => [
  {
    id: 0,
    name: 'idin',
    label: I18n.t('users.opp_onboarding.identity_verification.idin'),
    extraSelected: issuerSelected
  },
  {
    id: 1,
    name: 'document',
    label: I18n.t('users.opp_onboarding.identity_verification.document'),
    extraSelected: ''
  }
]

type State = {
  idealIssuers: AsyncState<IdealIssuerResponse>
  complianceResponse: AsyncState<ComplianceResponse>
  selectedMethod: 'none' | MethodKey
  issuerSelected: string
}

const initialState = (props: Props): State => ({
  idealIssuers: loadingAsyncState(_ => getIdealIssuers()),
  complianceResponse: loadingAsyncState(_ => getCompliance(props.id)),
  selectedMethod: 'none',
  issuerSelected: ''
})

const getErrorFromState = (s0: State): Option<string> => {
  if (
    s0.complianceResponse.kind === 'loaded' &&
    !contactRequirement(s0.complianceResponse.value.compliance)
  ) {
    return some(
      I18n.t(
        'users.opp_onboarding.identity_verification.no_verification_needed'
      )
    )
  }

  if (
    ['connection-error', 'unauthorized', 'failed'].includes(
      s0.complianceResponse.kind
    )
  ) {
    return some(
      I18n.t('users.opp_onboarding.identity_verification.generic_error')
    )
  }
  return none()
}

const chooseOptionProps = (
  props: Props,
  s0: State,
  setState: (_: Action<State>) => void,
  idealIssuers: IdealIssuer[]
): ChooseOptionProps<Method> => ({
  selected: s0.selectedMethod,
  options: methods(s0.issuerSelected),
  error: getErrorFromState(s0),
  title: I18n.t('users.opp_onboarding.identity_verification.title'),
  intro: I18n.t('users.opp_onboarding.identity_verification.intro'),
  submitLabel: I18n.t(
    'users.opp_onboarding.identity_verification.submit_label'
  ),
  emptyExtraOptionLabel: I18n.t(
    'users.opp_onboarding.identity_verification.empty_extra'
  ),
  selectThis: (ii: MethodKey) => setState(s => ({ ...s, selectedMethod: ii })),
  selectThisExtraFor: (_, issuerSelected: string) =>
    setState(s => ({ ...s, issuerSelected })),
  onSubmit: () => {
    if (s0.complianceResponse.kind === 'loaded') {
      const contact = contactRequirement(s0.complianceResponse.value.compliance)
      const selectedIssuer = idealIssuers.find(
        ii => ii.name === s0.issuerSelected
      )
      if (!contact || (s0.selectedMethod === 'idin' && !selectedIssuer)) {
        setState(s => ({ ...s, errors: true }))
      } else {
        DL.push({
          event: 'OPP',
          category: 'Onboarding OPP',
          action: 'Identification',
          label: s0.selectedMethod
        })
        window.location.href =
          contact.object_redirect_url +
          (s0.selectedMethod === 'idin'
            ? `?verification_type=idin&issuer=${selectedIssuer.bic}`
            : '') // note: this seems to ignore the return_url
      }
    }
  },
  formDisabled:
    s0.complianceResponse.kind === 'loaded' &&
    !contactRequirement(s0.complianceResponse.value.compliance),
  submitDisabled:
    s0.complianceResponse.kind !== 'loaded' ||
    s0.selectedMethod === 'none' ||
    (s0.selectedMethod === 'idin' && s0.issuerSelected === ''),
  images: Immutable.Map({
    idin: props.idinImage
  }),
  extraSelections: Immutable.Map({
    idin: idealIssuers.map((ii, i) => ({
      id: i,
      name: ii.name,
      label: ii.name,
      extraSelected: ''
    }))
  })
})

const complianceLoader = (s0: State): Widget<Action<State>> =>
  async<ComplianceResponse>()(s0.complianceResponse).map(uc => s => {
    const complianceResponse = uc(s.complianceResponse)
    return {
      ...s,
      complianceResponse
    }
  })

const issuersLoader = (s0: State): Widget<Action<State>> =>
  async<IdealIssuerResponse>(_ => nothing())(s0.idealIssuers).map(
    updateIssuers => s => ({
      ...s,
      idealIssuers: updateIssuers(s.idealIssuers)
    })
  )

const chooseOptionWidget = (props: Props, s0: State): Widget<Action<State>> =>
  fromJSX(setState =>
    <div className="page-section page-section--pt">
      {s0.idealIssuers.kind === 'loaded' ? (
        <ChooseOptionComponent<Method>
          {...chooseOptionProps(
            props,
            s0,
            setState,
            s0.idealIssuers.value.ideal_issuers
          )}
        />
      ) : (
        <LoaderCircle />
      )}
    </div>
  )
export class IdentityVerification extends React.Component<Props> {
  render() {
    return (
      <>
        {stateful<State>({ key: 'identity_verification' })(s0 =>
          any<Action<State>>()([
            complianceLoader(s0),
            issuersLoader(s0),
            chooseOptionWidget(this.props, s0)
          ]).map(a => a(s0))
        )(initialState(this.props)).run(_ => {})}
      </>
    )
  }
}

export default (props: Props): JSX.Element => (
  <IntlProvider locale={window.Gites.locale}>
    <IdentityVerification {...props} />
  </IntlProvider>
)
